import { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    userRole: null,
    userID: null,
    isLoading: true, // Only for initial load
  });


  // Single source of truth for auth status
  const checkAuth = async () => {
    try {
      const authStatusString = localStorage.getItem('authStatus');
      
      if (!authStatusString) {
        return { isAuthenticated: false, userRole: null, userID: null };
      }

      const authStatus = JSON.parse(authStatusString);
      return { 
        isAuthenticated: authStatus.isLoggedIn, 
        userRole: authStatus.role,
        userID: authStatus.Id 
      };
    } catch (err) {
      console.error('Auth check error:', err);
      return { isAuthenticated: false, userRole: null, userID: null };
    }
  };

  // Initialize auth state only once
  useEffect(() => {
    const initializeAuth = async () => {
      const { isAuthenticated, userRole, userID } = await checkAuth();
      setAuthState({
        isAuthenticated,
        userRole,
        userID,
        isLoading: false
      });
    };
    
    initializeAuth();
  }, []); // Empty dependency array ensures this runs only once

  const login = async (credentials) => {
    try {
      const formData = new FormData();
      formData.append('email', credentials.email);
      formData.append('password', credentials.password);
      formData.append('captcha', credentials.captcha);
      formData.append('action', 'login');
      formData.append('token', `${process.env.REACT_APP_API_TOKEN}`);

      const response = await axios.post(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        formData,
        { withCredentials: true }
      );
      
      if (response.data?.success) {
        const userRole = response.data.data?.user_role;
        const userID = response.data.data?.user_id;
        localStorage.setItem('authStatus', JSON.stringify({
          isLoggedIn: true,
          role: userRole,
          Id: userID
        }));
        
        setAuthState({
          isAuthenticated: true,
          userRole,
          userID,
          isLoading: false
        });
        
        return { success: true, role: userRole, Id: userID };
      }
      return { success: false, data: response };
    } catch (err) {
      console.error('Login error:', err);
      return { success: false };
    }
  };

  const logout = async () => {
    try {
      await axios.post(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        { action: 'logout' },
        { withCredentials: true }
      );
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('authStatus');
      setAuthState({
        isAuthenticated: false,
        userRole: null,
        isLoading: false
      });
    }
  };

  const getTeams = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getTeam', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Team Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getProducts = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getProducts', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Products Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getUsers = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getUsers', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Users Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getStudents = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getStudents', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Students Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getParents = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getStudents', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Parents Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getSettings = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getSettings', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getBackgrounds = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getBackgrounds', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const addBackground = async (credentials) => {
    try {
      const formData = new FormData();
      formData.append('title', credentials.title);
      formData.append('image', credentials.image);
      formData.append('page_name', credentials.page_name);
      formData.append('date_created', credentials.date_created);
      formData.append('action', 'addBackgrounds');
      formData.append('token', `${process.env.REACT_APP_API_TOKEN}`);

      const response = await axios.post(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          formData,
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getServices = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getServices', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getModules = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getModules', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getTestmonials = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getTestmonials', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getAboutus = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getAboutus', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getWhysteam = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getWhysteam', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getDepartments = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getDepartments', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  // ###################################################################################################
  const getLeaves = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getLeaves', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getLaptops = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getLaptops', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getChargers = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getChargers', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getMonitors = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getMonitors', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getComputers = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getComputers', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getBlogs = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getBlogs', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getAddverts = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getAddverts', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getBorrows = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getBorrows', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getReturns = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getReturns', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getTables = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getTables', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getChairs = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getChairs', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  // ###########################################################################################################
  const addData = async (endpoint, tableName, credentials) => {
    try {
      const formData = new FormData();
      
      // Append all provided credentials to formData
      for (const key in credentials) {
        if (credentials.hasOwnProperty(key)) {
          formData.append(key, credentials[key]);
        }
      }

      // System fields
      formData.append('action', `addDatas`);
      formData.append('table', tableName);
      formData.append('token', process.env.REACT_APP_API_TOKEN);

      const response = await axios.post(
        `https://mindsinaction.com.na/api/mindsbackend/${endpoint}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error",
        errors: response.data?.errors
      };
    } catch (err) {
      console.error(`Error adding ${tableName} Data:`, err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error",
        status: err.response?.status
      };
    }
  };

  const resetPassword = async (endpoint, tableName, credentials) => {
    try {
      const formData = new FormData();

      for (const key in credentials) {
        if (credentials.hasOwnProperty(key)) {
          formData.append(key, credentials[key]);
        }
      }

      // System fields
      formData.append('action', `staffsPasswordReset`);
      formData.append('table', tableName);
      formData.append('token', process.env.REACT_APP_API_TOKEN);

      const response = await axios.post(
        `https://mindsinaction.com.na/api/mindsbackend/${endpoint}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error",
        errors: response.data
      };
    } catch (err) {
      console.error(`Error reseting ${tableName} Data:`, err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error",
        status: err.response?.status
      };
    }
  };

  const updateData = async (endpoint, tableName, credentials, id) => {
    try {
      const formData = new FormData();
      
      // Append all provided credentials to formData
      for (const key in credentials) {
        if (credentials.hasOwnProperty(key)) {
          formData.append(key, credentials[key]);
        }
      }

      // System fields
      formData.append('action', 'updateDatas');
      formData.append('table', tableName);
      formData.append('token', process.env.REACT_APP_API_TOKEN);
      formData.append('id', id); // ID of the record to update

      console.log("responce: ",formData.image);
      const response = await axios.post(
        `https://mindsinaction.com.na/api/mindsbackend/${endpoint}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      } 
      else{
        console.log("responce: ",response);
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error",
        errors: response.data?.errors
      };
    } catch (err) {
      console.error(`Error updating ${tableName} Data:`, err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error",
        status: err.response?.status
      };
    }
  };

  const getData = async (endpoint, tableName, id = null) => {
    try {
      const formData = new FormData();
      formData.append('action', 'getDatas'); // Confirm correct action name
      formData.append('table', tableName);  // Confirm correct table param name
      formData.append('token', process.env.REACT_APP_API_TOKEN);

      // Append additional filters (if any)
      if (id !== null) {
        formData.append('id', id);
      }

      const response = await axios.post(
        `https://mindsinaction.com.na/api/mindsbackend/${endpoint}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        }
      );

      if (!response.data.success) {
        //console.log(response.data.data);
        
        throw new Error(response.data.message || "API rejected request");
      }

      return {
        success: true,
        data: response.data.data || [],
        message: response.data.message,
      };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || err.message || "Request failed",
        errors: err.response?.data?.errors || [],
      };
    }
  };

  const deleteData = async (endpoint, tableName, id) => {
    try {
      const formData = new FormData();
      
      // System fields
      formData.append('action', 'deleteData');
      formData.append('table', tableName);
      formData.append('token', process.env.REACT_APP_API_TOKEN);
      formData.append('id', id); // ID of the record to delete

      const response = await axios.post(
        `https://mindsinaction.com.na/api/mindsbackend/${endpoint}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error",
        errors: response.data?.errors
      };
    } catch (err) {
      console.error(`Error deleting ${tableName}:`, err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error",
        status: err.response?.status
      };
    }
  };

  // ##############################################################################################################

  return (
    <AuthContext.Provider value={{ ...authState, login, logout, getTeams, getProducts, getStudents, getUsers, getSettings, getParents, getBackgrounds, addBackground, getServices, getModules, getTestmonials, getAboutus, getWhysteam, getDepartments, getAddverts, getLeaves, getBlogs, getBorrows, getReturns, getTables, getChairs, addData, getData, updateData, deleteData, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);